import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, EventClickArg, EventInput } from '@fullcalendar/common';
import { DateClickArg } from '@fullcalendar/interaction';
import { combineLatest, ReplaySubject, Subscription } from 'rxjs';
import { Calendar, CalendarEntry } from 'src/app/classes';
import { CalendarViews } from 'src/app/components/widget/widget-inner-components/calendar-widget-inner/calendar-widget-inner.component';
import { CalendarService } from 'src/app/lib/nextcloud/services/calendar.service';
import * as moment from 'moment';
import { Guid } from 'src/app/globals';
import { showAnimation } from 'src/animations';
import { delay, map, switchMap, take, tap } from 'rxjs/operators';
import { Step, STEP_FORM_GROUP } from '../../dialog/issue-transaction-dialog2/issue-transaction-dialog2-component-classes';

@Component({
  selector: 'app-make-appointment-form',
  templateUrl: './make-appointment-form.component.html',
  styleUrls: ['./make-appointment-form.component.css'],
  animations: [showAnimation]
})
export class MakeAppointmentFormComponent implements OnDestroy, OnInit {

  private _calendarComponent = new ReplaySubject<FullCalendarComponent>();
  calendarComponent$ = this._calendarComponent.asObservable();

  @ViewChild("calendar") set calendarComponent(calendar: FullCalendarComponent) {
    this._calendarComponent.next(calendar);
  }
  @ViewChild("calendarWrapper", { 'static': true }) calendarWrapper: ElementRef<HTMLDivElement>;

  currentTaskIndex: number = null;

  calendarOptions: CalendarOptions = {
    locale: 'de',
    initialView: CalendarViews.DayGridMonth,
    firstDay: 1,
    eventClassNames: "calendar-event-entry",
    eventClick: (arg: EventClickArg) => this.eventClicked(arg),
    dateClick: (arg: DateClickArg) => this.dateClick(arg),
    customButtons: {
      prev: {
        click: () => {
          this.calendarComponent$.pipe(take(1)).subscribe((calendarComponent) => {
            calendarComponent.getApi().prev();
            this.resize();
          });
        }
      },
      next: {
        click: () => {
          this.calendarComponent$.pipe(take(1)).subscribe((calendarComponent) => {
            calendarComponent.getApi().next();
            this.resize();
          });
        }
      }
    },
    headerToolbar: {
      left: 'title',
      center: '',
      right: 'prev,next'
    },
    viewDidMount: () => {
      this.resize();
    }
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(STEP_FORM_GROUP) public formGroup: FormGroup,
    @Inject('step') private _step: Step,
    private _calendarService: CalendarService,
    private _formBuilder: FormBuilder
  ) { }

  addEventToCalendar(event: CalendarEntry, calendarComponent: FullCalendarComponent, unpersisted = false) {
    if (!calendarComponent.getApi().getEvents().find(x => x.id === event.id)) {
      calendarComponent
        .getApi()
        .addEvent({
          color: event.calendar ? event.calendar.color : null,
          id: event.id,
          title: event.title,
          start: event.start,
          end: event.end,
          className: unpersisted ? 'unpersisted-event' : 'persisted'
        } as EventInput);
    }
  }

  closeEventDialog() {
    this.currentTaskIndex = null;
  }

  dateClick(arg: DateClickArg) {
    this
      ._calendarService
      .loadCalendars(true)
      .subscribe((calendars: Calendar[]) => {
        this.appointmentsControl.push(this._formBuilder.group({
          'id': Guid.newGuid(),
          'start': moment(arg.dateStr).format('YYYY-MM-DDT10:mm:ss'),
          'end': moment(arg.dateStr).format('YYYY-MM-DDT12:mm:ss'),
          'title': null,
          'calendarIdentifier': [calendars[0] ? calendars[0].href : null, Validators.required],
          'calendar': calendars[0],
          'href': calendars[0] ? `${calendars[0].href}/${Guid.newGuid()}.ics` : null
        }));
        this.currentTaskIndex = this.appointmentsControl.controls.length - 1;
      });
  }

  deleteEvent() {
    this.calendarComponent$.pipe(take(1)).subscribe((calendarComponent) => {
      let calendarEvent = calendarComponent
        .getApi()
        .getEvents()
        .find(x => x.id === this.appointmentsControl.controls[this.currentTaskIndex].value.id);

      if (calendarEvent) calendarEvent.remove();

      this.appointmentsControl.removeAt(this.currentTaskIndex);
      this.currentTaskIndex = null;
    });
  }

  eventClicked(event: EventClickArg) {
    let index = this
      .appointmentsControl
      .controls
      .findIndex(x => x.value.id === event.event.id);

    if (index > -1) this.currentTaskIndex = index;
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
    this._calendarComponent.complete();
  }

  ngOnInit(): void {
    this._subscriptions.push(...[
      combineLatest([
        this
          ._calendarService
          .loadCalendars(true)
          .pipe(switchMap((calendars: Calendar[]) => combineLatest(calendars.map(x => this._calendarService.loadCalendarContent(x.href))).pipe(map(() => calendars)))),
        this.calendarComponent$
      ]).pipe(delay(200)).subscribe(([calendars, calendarComponent]) => this.setCalendarEvents(calendars, calendarComponent)),
      this.appointmentsControl.valueChanges.subscribe((value: CalendarEntry[]) => this._step.visibleInConclusion = value.length > 0? true: false)
    ]);
  }

  resize() {
    this.calendarComponent$.pipe(take(1)).subscribe((calendarComponent: FullCalendarComponent) => {
      calendarComponent.getApi().setHeight(this.calendarWrapper.nativeElement.clientHeight);
      calendarComponent.getApi().updateSize();
    });
  }

  saveEvent() {
    this.calendarComponent$.pipe(take(1)).subscribe((calendarComponent: FullCalendarComponent) => {
      this.addEventToCalendar(this.appointmentsControl.controls[this.currentTaskIndex].value, calendarComponent, true);
      this.currentTaskIndex = null;
      this.resize();
    });
  }

  setCalendarEvents(calendars: Calendar[], calendarComponent: FullCalendarComponent) {

    calendarComponent.getApi().removeAllEvents();

    for (let control of (this.appointmentsControl.controls as AbstractControl[])) this.addEventToCalendar(control.value, calendarComponent, true);

    combineLatest(calendars.map(calendar => this._calendarService.loadCalendarContent(calendar.href).pipe(tap((events: CalendarEntry[]) => {
      events.forEach(x => this.addEventToCalendar(x, calendarComponent));
    })))).subscribe(() => this.resize());
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.resize();
  }

  get appointmentsControl(): FormArray {
    return this.formGroup.controls['appointments'] as FormArray;
  }

}
